import { ofetch } from 'ofetch'
import {
  checkHaveToken,
  clearToken,
  getRefreshToken,
  getToken,
  setRefreshToken,
  setToken,
} from '@/utils/token'
import { serialize } from 'cookie-es'
import { clearAbilityRulesCookie } from '@/plugins/ability'
import { getApiBaseUrl, getClientId, getCurrentLanguage, isBlob } from '@/utils/helpers'

const toast = useToast()
const apiBaseUrl = getApiBaseUrl()

export const ACCESS_TOKEN_EXPIRED_CODE = 4012

/** Session auth failures that require logout — not access-token expiry (4012). */
const SESSION_AUTH_ERROR_CODES = new Set([401, 410, 4011])

function resolveRequestUrl(request) {
  if (typeof request === 'string') return request
  return request?.url ?? String(request ?? 'unknown')
}

export function wasApiErrorToastShown(data) {
  return Boolean(data?.__apiErrorToastShown)
}

export function showCallerApiErrorToast(data, fallbackMessage) {
  if (wasApiErrorToastShown(data)) {
    return
  }

  toast.error(data?.message || fallbackMessage)
}

function markApiErrorToastShown(data) {
  if (!data || typeof data !== 'object') {
    return data
  }

  return { ...data, __apiErrorToastShown: true }
}

// Dedupe: only show one invalid-token toast and run handleTokenExpired once when multiple requests fail (use window so state is shared across module instances)
const getTokenExpiredHandled = () => (typeof window !== 'undefined' && window.__apiTokenExpiredHandled) || false
const setTokenExpiredHandled = () => { if (typeof window !== 'undefined') window.__apiTokenExpiredHandled = true }
const getTokenExpiredToastShown = () => (typeof window !== 'undefined' && window.__apiTokenExpiredToastShown) || false
const setTokenExpiredToastShown = () => { if (typeof window !== 'undefined') window.__apiTokenExpiredToastShown = true }

/** M-04: Reset token-expiry latch so a new login session can trigger cleanup/toast on expiry. Call after successful login. */
export function resetTokenExpiredState() {
  if (typeof window === 'undefined') return
  delete window.__apiTokenExpiredHandled
  delete window.__apiTokenExpiredToastShown
}

function handleTokenExpired() {
  if (getTokenExpiredHandled()) return
  setTokenExpiredHandled()

  clearToken()
  clearAbilityRulesCookie()

  if (typeof document !== 'undefined') {
    document.cookie = serialize('accessToken', '', {
      path: '/',
      maxAge: -1,
    })
  }

  const { gotoLogin } = useGoto()
  gotoLogin()
}

let tokenExpiredMessage = null
/** True while the shared refresh call is in flight. */
let accessTokenRefreshInFlight = false
/** Active 4012 recoveries (refresh + retry) — suppress expiry toasts until each retry finishes. */
let tokenRecoveryInFlightCount = 0

function isSilentTokenRecoveryActive() {
  return accessTokenRefreshInFlight || tokenRecoveryInFlightCount > 0
}

function flushTokenExpiredToast() {
  if (tokenExpiredMessage === null) return
  const msg = tokenExpiredMessage
  tokenExpiredMessage = null
  toast.error(msg || 'Token expired', () => {
    handleTokenExpired()
  })
}

function showTokenExpiredToast(message, { force = false } = {}) {
  if (!force && isSilentTokenRecoveryActive()) {
    return
  }
  const alreadyShown = getTokenExpiredToastShown()
  setTokenExpiredToastShown()
  if (alreadyShown) return
  tokenExpiredMessage = message || 'Token expired'
  queueMicrotask(flushTokenExpiredToast)
}

function buildApiHeaders(includeAuth = false) {
  const currentLang = getCurrentLanguage()
  const headers = {
    'Content-Type': 'application/json',
    'xxx-device-id': '',
    'xxx-client-id': getClientId(),
    'Accept-Language': currentLang === 'en' ? 'en-US' : 'pt-BR',
  }

  if (includeAuth && checkHaveToken()) {
    headers.Authorization = `Bearer ${getToken()}`
  }

  return headers
}

async function performTokenRefresh() {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  const res = await ofetch('/api/v1/oauth2/refresh', {
    baseURL: apiBaseUrl,
    method: 'POST',
    body: { refresh_token: refreshToken },
    headers: buildApiHeaders(false),
  })

  const refreshError = res?.data?.error
  if (refreshError != null) {
    throw new Error(refreshError?.errorMessage || res?.message || 'Token refresh failed')
  }

  if (res?.code !== 200) {
    throw new Error(res?.message || 'Token refresh failed')
  }

  const tokenInfo = res?.data?.token_info
  if (!tokenInfo?.access_token) {
    throw new Error(res?.message || 'Token refresh failed')
  }

  setToken(tokenInfo.access_token)

  if (tokenInfo.refresh_token) {
    setRefreshToken(tokenInfo.refresh_token)
  }

  return res
}

let refreshPromise = null

async function refreshAccessToken() {
  if (!refreshPromise) {
    accessTokenRefreshInFlight = true
    refreshPromise = performTokenRefresh().finally(() => {
      refreshPromise = null
      accessTokenRefreshInFlight = false
    })
  }

  return refreshPromise
}

function isRefreshRequest(options) {
  return options?._isRefreshRequest === true
}

function isRetriedRequest(options) {
  return options?._retried === true
}

/** Retry opts without abort signal/controller — fresh request after token refresh. */
function buildRetryRequestOptions(options = {}) {
  const {
    signal: _signal,
    _controller,
    _retried: _alreadyRetried,
    ...retryOptions
  } = options

  return {
    ...retryOptions,
    _retried: true,
  }
}

async function handleAccessTokenExpired({ request, options, responseData }) {
  if (isRefreshRequest(options) || isRetriedRequest(options)) {
    showTokenExpiredToast(responseData?.message, { force: true })
    return markApiErrorToastShown(responseData)
  }

  try {
    await refreshAccessToken()
    tokenRecoveryInFlightCount++
    try {
      return await $api(resolveRequestUrl(request), buildRetryRequestOptions(options))
    } finally {
      tokenRecoveryInFlightCount--
    }
  } catch (err) {
    showTokenExpiredToast(responseData?.message || 'Token expired', { force: true })
    return markApiErrorToastShown(responseData)
  }
}

export const $api = ofetch.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
    'xxx-device-id': '',
  },
  timeout: 60000,
  async onRequest({ request, options }) {
    const controller = new AbortController()
    options.signal = controller.signal
    options._controller = controller
    options.headers = {
      ...buildApiHeaders(true),
      ...(options.headers || {}),
    }

    if (options._authToken) {
      options.headers.Authorization = `Bearer ${options._authToken}`
    }
  },
  async onResponse({ request, response, options }) {
    if (response.status === 200) {
      if (isBlob(response._data)) {
        return response._data
      }

      if (response._data?.code === ACCESS_TOKEN_EXPIRED_CODE) {
        response._data = await handleAccessTokenExpired({
          request,
          options,
          responseData: response._data,
        })
        return response._data
      }

      if (SESSION_AUTH_ERROR_CODES.has(response._data?.code)) {
        showTokenExpiredToast(response._data.message, { force: true })
        return markApiErrorToastShown(response._data)
      }

      if (response._data?.code === 403) {
        toast.error(response._data.message || 'Permission denied')
        response._data = markApiErrorToastShown(response._data)
        return response._data
      }

      return response._data
    }
  },
  async onResponseError({ request, response, error, options }) {
    if (response?.status === 401) {
      handleTokenExpired()
      return
    }

    if (response?.status === 403) {
      toast.error(response._data?.message || 'Permission denied')
      response._data = markApiErrorToastShown(response._data)
      return
    }

    if (response?._data?.code === ACCESS_TOKEN_EXPIRED_CODE && !isRefreshRequest(options) && !isRetriedRequest(options)) {
      return await handleAccessTokenExpired({
        request,
        options,
        responseData: response._data,
      })
    }

    if (response && response._data && response._data.message) {
      const msg = response._data.message
      const isTokenError = SESSION_AUTH_ERROR_CODES.has(response._data.code)
        || /invalid token|token expired|unauthorized/i.test(msg || '')
      if (isTokenError) {
        showTokenExpiredToast(msg)
      } else {
        toast.error(msg)
      }
    } else if (response && response.statusText) {
      toast.error(response.statusText)
    } else if (error && error.name !== 'AbortError') {
      toast.error('Request failed')
    }
  },
})
